// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Social sign-in (Google / Sign in with Apple) via the Cognito managed-login
 * domain — authorization-code + PKCE, shared by web and mobile.
 *
 * The embedded email/password flow (client.ts) is untouched: social sign-in
 * is the ONE case where a redirect to the Cognito domain is required, because
 * the federation handshake with Google/Apple has to happen on a domain both
 * sides trust (ADR-014 stays authoritative for the first-party flow). The
 * tokens that come back are the same Cognito token triple, so callers feed
 * them through the exact same session layer (`applySession` on web,
 * `writeSession` on mobile) as a password login.
 *
 * Dependency-free like the rest of this package: SHA-256 for the PKCE
 * challenge is implemented here (FIPS 180-4) because React Native's Hermes
 * runtime has no WebCrypto `crypto.subtle`, and pulling in expo-crypto would
 * fork the implementation between platforms. The pure-JS digest is
 * deterministic and covered by known-answer tests.
 */
import { CognitoError } from "./client";
/** Runtime-config names (aws-exports.json / app.json extra) → Cognito identity_provider. */
export const SOCIAL_PROVIDER_NAMES = {
    google: "Google",
    apple: "SignInWithApple",
};
/** Normalizes a runtime-config provider list; unknown names are dropped (fail-closed: no button). */
export function parseSocialProviders(raw) {
    if (!Array.isArray(raw))
        return [];
    const providers = [];
    for (const entry of raw) {
        if (typeof entry !== "string")
            continue;
        const mapped = SOCIAL_PROVIDER_NAMES[entry.trim().toLowerCase()];
        if (mapped && !providers.includes(mapped))
            providers.push(mapped);
    }
    return providers;
}
// ---------------------------------------------------------------------------
// SHA-256 (FIPS 180-4) — pure JS so web and Hermes share one implementation.
// ---------------------------------------------------------------------------
// First 32 bits of the fractional parts of the cube roots of the first 64 primes.
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
function rotr(x, n) {
    return (x >>> n) | (x << (32 - n));
}
/** SHA-256 digest of `bytes`. Synchronous, allocation-light, standards-exact. */
export function sha256(bytes) {
    const bitLength = bytes.length * 8;
    // Message + 0x80 + zero pad to 56 mod 64 + 8-byte big-endian bit length.
    const paddedLength = (((bytes.length + 8) >> 6) + 1) << 6;
    const padded = new Uint8Array(paddedLength);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
    view.setUint32(paddedLength - 4, bitLength >>> 0);
    const h = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const w = new Uint32Array(64);
    for (let offset = 0; offset < paddedLength; offset += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = view.getUint32(offset + i * 4);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = h;
        for (let i = 0; i < 64; i++) {
            const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const temp1 = (hh + s1 + ch + K[i] + w[i]) >>> 0;
            const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (s0 + maj) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }
    const out = new Uint8Array(32);
    const outView = new DataView(out.buffer);
    for (let i = 0; i < 8; i++)
        outView.setUint32(i * 4, h[i]);
    return out;
}
// ---------------------------------------------------------------------------
// PKCE
// ---------------------------------------------------------------------------
/** RFC 4648 §5 base64url without padding — the PKCE/`state` alphabet. */
export function base64UrlEncode(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    // btoa exists in browsers and Hermes; Node ≥16 has Buffer as fallback for tests.
    const b64 = typeof btoa === "function"
        ? btoa(binary)
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            globalThis.Buffer.from(bytes).toString("base64");
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
/**
 * Cryptographically-strong random bytes where the runtime provides them
 * (every browser; Hermes with the Expo polyfill). The Math.random fallback
 * mirrors the guarded `safeUUID` precedent (OWNER_REVIEW D-5) — PKCE values
 * are single-use, short-lived, and only weaken the (already TLS-protected)
 * code-interception defense, never token custody.
 */
export function randomBytes(length) {
    const out = new Uint8Array(length);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(out);
    }
    else {
        for (let i = 0; i < length; i++)
            out[i] = Math.floor(Math.random() * 256);
    }
    return out;
}
export function createPkcePair(verifierBytes = randomBytes(32)) {
    const verifier = base64UrlEncode(verifierBytes);
    const ascii = new Uint8Array(verifier.length);
    for (let i = 0; i < verifier.length; i++)
        ascii[i] = verifier.charCodeAt(i);
    return { verifier, challenge: base64UrlEncode(sha256(ascii)) };
}
/** Opaque CSRF `state` value for the authorization request. */
export function randomState() {
    return base64UrlEncode(randomBytes(16));
}
function domainOrigin(domain) {
    return domain.startsWith("https://") ? domain.replace(/\/$/, "") : `https://${domain}`;
}
/** /oauth2/authorize URL that skips the hosted account-chooser and goes straight to the provider. */
export function buildAuthorizeUrl(params) {
    const query = new URLSearchParams({
        response_type: "code",
        client_id: params.clientId,
        redirect_uri: params.redirectUri,
        identity_provider: params.identityProvider,
        scope: (params.scopes ?? ["openid", "email", "profile"]).join(" "),
        state: params.state,
        code_challenge_method: "S256",
        code_challenge: params.codeChallenge,
        // Owner fix 2026-08-29: without this, Google silently signs in with the
        // browser's default account — users with several Google accounts never
        // get to choose. Cognito forwards prompt=select_account to the IdP on
        // managed-login pools (docs: authorization-endpoint, "prompt"); it has
        // no effect on local password sign-in.
        prompt: params.prompt ?? "select_account",
    });
    return `${domainOrigin(params.domain)}/oauth2/authorize?${query.toString()}`;
}
/**
 * Exchanges the authorization code at /oauth2/token. No client secret exists
 * (public SPA/native client); PKCE is the proof of possession. The result is
 * shaped exactly like `client.ts` sign-in results so it can be handed to the
 * same session-persistence path.
 */
export async function exchangeAuthorizationCode(params) {
    const fetchImpl = params.fetchImpl ?? fetch;
    const response = await fetchImpl(`${domainOrigin(params.domain)}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
            grant_type: "authorization_code",
            client_id: params.clientId,
            redirect_uri: params.redirectUri,
            code: params.code,
            code_verifier: params.codeVerifier,
        }).toString(),
    });
    const json = (await response.json().catch(() => ({})));
    if (!response.ok || json.error) {
        // The token endpoint reports OAuth error codes ("invalid_grant", …), not
        // Cognito exception names; keep the code verbatim for error mapping.
        throw new CognitoError(json.error ?? "OAuthTokenError", "OAuth code exchange failed");
    }
    if (!json.id_token || !json.access_token || !json.refresh_token) {
        throw new CognitoError("OAuthTokenError", "OAuth token response was incomplete");
    }
    return {
        idToken: json.id_token,
        accessToken: json.access_token,
        refreshToken: json.refresh_token,
        expiresInSeconds: json.expires_in ?? 3600,
        tokenType: json.token_type ?? "Bearer",
    };
}
